<template>
  <div class="upload-attachment-container">
    <div class="attachment-list flex-ct wrap">
      <div v-for="(file, index) in fileList" :key="file.uid || index" class="attachment-item mr10">
        <template v-if="file.status === 'uploading'">
          <div class="uploading-box flex-ct">
            <a-spin />
          </div>
        </template>
        <template v-else>
          <div class="file-content">
            <ImagePreview
              v-if="isImage(file.url)"
              :src="file.url"
              :imageList="imageList"
              :currentIndex="getImageIndex(file.url)"
              :thumbnailSize="104"
            />
            <div v-else class="file-placeholder flex-ct column" @click="downloadFile(file.url)">
              <a-icon type="file" style="font-size: 32px; color: #999" />
              <div class="file-name" :title="file.name">{{ file.name }}</div>
            </div>
            <div class="file-actions" v-if="!disabled">
              <a-icon type="delete" class="delete-icon" @click="handleRemove(file)" />
            </div>
          </div>
        </template>
      </div>

      <a-upload
        v-if="fileList.length < maxCount && !disabled"
        list-type="picture-card"
        :show-upload-list="false"
        :multiple="true"
        :custom-request="changeUpload"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        class="attachment-uploader"
      >
        <div>
          <a-icon :type="loading ? 'loading' : 'plus'" />
          <div class="ant-upload-text">{{ $t('上传') }}</div>
        </div>
      </a-upload>
    </div>
  </div>
</template>

<script>
import ImagePreview from './imagePreview.vue'
import http from '@/service/axios'
import api from '@/service/api'

export default {
  name: 'UploadAttachment',
  components: {
    ImagePreview,
  },
  model: {
    prop: 'url',
    event: 'update:url',
  },
  props: {
    url: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxCount: {
      type: Number,
      default: 9,
    },
  },
  data() {
    return {
      fileList: [],
      loading: false,
    }
  },
  computed: {
    imageList() {
      return this.fileList.filter((item) => this.isImage(item.url)).map((item) => item.url)
    },
  },
  watch: {
    url: {
      handler(val) {
        if (val) {
          const urls = val.split(';').filter((i) => i)
          const currentUrls = this.fileList
            .filter((f) => f.status === 'done')
            .map((f) => f.url)
            .join(';')
          if (val !== currentUrls) {
            this.fileList = urls.map((u, index) => ({
              uid: -index,
              name: u.split('/').pop(),
              status: 'done',
              url: u,
            }))
          }
        } else {
          this.fileList = []
        }
      },
      immediate: true,
    },
  },
  methods: {
    isImage(url) {
      return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url || '')
    },
    getImageIndex(url) {
      return this.imageList.indexOf(url)
    },
    async changeUpload(info) {
      const { file, onSuccess, onError } = info
      this.loading = true
      // 先推入一个正在上传的状态
      const uploadingFile = {
        uid: file.uid,
        name: file.name,
        status: 'uploading',
        url: '',
      }
      this.fileList.push(uploadingFile)

      await http({
        url: api.filePreUpload,
        data: {
          file_name: file.name,
          appCode: 'FMS',
          'Content-Type': file.type,
        },
        success: async (res) => {
          try {
            await fetch(res.result.upload_url, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': res.result.content_type,
              },
            })
            uploadingFile.url = res.result.image_url
            uploadingFile.status = 'done'
            this.updateUrl()
            onSuccess(res, file)
          } catch (e) {
            uploadingFile.status = 'error'
            onError(e)
          }
        },
        error: (e) => {
          uploadingFile.status = 'error'
          onError(e)
        },
      })
      this.loading = false
    },
    handleRemove(file) {
      const index = this.fileList.findIndex((item) => item.uid === file.uid)
      if (index !== -1) {
        this.fileList.splice(index, 1)
        this.updateUrl()
      }
    },
    updateUrl() {
      const urlStr = this.fileList
        .filter((item) => item.status === 'done')
        .map((item) => item.url)
        .join(';')
      this.$emit('update:url', urlStr)
    },
    downloadFile(url) {
      window.open(url)
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-attachment-container {
  .attachment-item {
    padding: 0;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    position: relative;
    // overflow: hidden;

    .file-content {
      width: 104px;
      height: 104px;
      position: relative;

      &:hover {
        .file-actions {
          display: flex;
        }
      }
    }

    .uploading-box {
      width: 100%;
      height: 100%;
    }

    .file-placeholder {
      width: 100%;
      height: 100%;
      cursor: pointer;
      .file-name {
        margin-top: 4px;
        font-size: 12px;
        color: #666;
        width: 100%;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .file-actions {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 20px;
      height: 20px;
      background: #ff4d4f;
      display: none;
      align-items: center;
      justify-content: center;
      z-index: 10;
      border-radius: 50%;
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      transition: all 0.2s;

      &:hover {
        background: #ff7875;
        transform: scale(1.1);
      }

      .delete-icon {
        color: #fff;
        font-size: 12px;
      }
    }
  }

  ::v-deep .attachment-uploader {
    .ant-upload.ant-upload-select-picture-card {
      width: 104px;
      height: 104px;
      margin: 0;
    }
  }
}
</style>
